import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import { selectIsAuthenticated, selectIsOrgOwner } from "../features/authentication/authenticationSlice";
import {
  selectAccountDeletionScheduledFor,
  selectAccountDeletionStatus,
} from "../features/account-deletion/account-deletion-slice";
import {
  cancelAccountDeletion,
  fetchAccountDeletionStatus,
} from "../features/account-deletion/slice-extensions/account-deletion-slice-extension";

/**
 * App-wide grace-window warning: while an account deletion is scheduled, every
 * instructor in the organization sees the date; the Owner can cancel in one click.
 */
const AccountDeletionBanner = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const scheduledFor = useAppSelector(selectAccountDeletionScheduledFor);
  const status = useAppSelector(selectAccountDeletionStatus);
  const isOwner = useAppSelector(selectIsOrgOwner);

  useEffect(() => {
    if (isAuthenticated && status === "idle") {
      dispatch(fetchAccountDeletionStatus());
    }
  }, [isAuthenticated, status, dispatch]);

  if (!isAuthenticated || !scheduledFor) {
    return null;
  }

  const date = new Intl.DateTimeFormat(i18n.language, { dateStyle: "long" }).format(
    new Date(scheduledFor),
  );

  return (
    <div className="flex flex-wrap items-center justify-center gap-2 bg-red-100 px-4 py-3 dark:bg-red-900/30">
      <p className="text-sm font-medium text-red-800 dark:text-red-300">
        {t("accountDeletion.bannerText", "This account and all its data will be deleted on {{date}}.", {
          date,
        })}
      </p>
      {isOwner && (
        <button
          type="button"
          className="text-sm font-semibold text-red-800 underline hover:no-underline dark:text-red-300"
          onClick={() => dispatch(cancelAccountDeletion())}
        >
          {t("accountDeletion.bannerCancel", "Cancel the deletion")}
        </button>
      )}
    </div>
  );
};

export default AccountDeletionBanner;
